import { Account, ACCOUNT_TYPE_LABELS } from '../../types/account';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatCurrency } from '../../lib/currency';
import { useTranslation } from 'react-i18next';

interface AccountCardProps {
  account: Account;
  onClick?: () => void;
  onEdit?: (account: Account) => void;
  onDelete?: (account: Account) => void;
}

export function AccountCard({ account, onClick, onEdit, onDelete }: AccountCardProps) {
  const { t } = useTranslation();

  const getBalanceColor = () => {
    if (account.type === 'credit_card') {
      return account.balance < 0 ? 'text-red-600' : 'text-green-600';
    }
    return account.balance >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(account);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(account);
  };

  return (
    <div
      className={`flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {account.color && (
          <div
            className="w-10 h-10 rounded-full flex-shrink-0"
            style={{ backgroundColor: account.color }}
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{account.name}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              {t(`accounts:types.${account.type}`)}
            </span>
          </div>
          {account.description && (
            <p className="text-sm text-gray-500 mt-0.5">{account.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className={`text-right font-bold text-xl ${getBalanceColor()}`}>
          {formatCurrency(account.balance)}
        </div>

        {onEdit && (
          <Button
            onClick={handleEditClick}
            variant="secondary"
            className="text-sm px-3 py-1"
          >
            {t('common:edit')}
          </Button>
        )}

        {onDelete && (
          <Button
            onClick={handleDeleteClick}
            variant="danger"
            className="text-sm px-3 py-1"
          >
            {t('common:delete')}
          </Button>
        )}
      </div>
    </div>
  );
}
